import React, { useMemo } from "react"
import { Table, Alert, Spin, Typography } from "@mimber/ui-web"
import { usePayoutTransactions } from "@mimber/client/mimber-organization"
import type { ColumnsType } from "@mimber/ui-web"
import type { BalanceTransactionDto } from "@mimber/schema"
import type { PayoutTransactionsTableProps } from "./PayoutTransactionsTable.types"
import { format, parseISO } from "date-fns"

const { Text } = Typography

export const PayoutTransactionsTable: React.FunctionComponent<
  PayoutTransactionsTableProps
> = ({ payoutId, className }) => {
  const { data, isLoading, error } = usePayoutTransactions(payoutId)
  const transactions = data?.data || []

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100)
  }

  const columns: ColumnsType<BalanceTransactionDto> = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (type: string) => type.replace(/_/g, " ").toUpperCase(),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
      },
      {
        title: "Amount",
        key: "amount",
        render: (_: unknown, record: BalanceTransactionDto) =>
          formatCurrency(record.amount, record.currency),
      },
      {
        title: "Fee",
        key: "fee",
        render: (_: unknown, record: BalanceTransactionDto) =>
          formatCurrency(record.fee, record.currency),
      },
      {
        title: "Net",
        key: "net",
        render: (_: unknown, record: BalanceTransactionDto) => (
          <Text strong>{formatCurrency(record.net, record.currency)}</Text>
        ),
      },
      {
        title: "Date",
        dataIndex: "created",
        key: "created",
        render: (date: string) => format(parseISO(date), "MMM d, yyyy HH:mm"),
      },
    ],
    []
  )

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spin tip="Loading transactions..." />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Transactions"
        description={error.message}
        type="error"
        showIcon
      />
    )
  }

  return (
    <Table
      dataSource={transactions}
      columns={columns}
      rowKey="id"
      pagination={false}
      size="small"
      className={className}
    />
  )
}
